import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { api, romeDateString, romeNow } from "@/lib/api";
import { useWS } from "@/contexts/WebSocketContext";
import { TrendingUp, UtensilsCrossed, Users, ChefHat, FileText, Calendar, Clock, ArrowUpRight, Sparkles, BellRing, Receipt as ReceiptIcon, BarChart3, Mic, MicOff } from "lucide-react";

const KpiCard = ({ icon: Icon, label, value, accent, hint, testId }) => (
    <div className="bg-surface border border-[#1f2c44] rounded-lg p-6 hover:border-brand/50 transition-colors" data-testid={testId}>
        <div className="flex items-start justify-between">
            <div>
                <div className="text-[10px] uppercase tracking-[0.2em] text-elegant mb-2">{label}</div>
                <div className={`font-display text-4xl font-bold ${accent || "text-warm"}`}>{value}</div>
                {hint && <div className="text-xs text-elegant mt-2">{hint}</div>}
            </div>
            <div className="w-12 h-12 rounded-md bg-[#1f1f1f] border border-[#1f2c44] flex items-center justify-center text-brand">
                <Icon className="w-5 h-5" />
            </div>
        </div>
    </div>
);

const Dashboard = () => {
    const [stats, setStats] = useState(null);
    const [logs, setLogs] = useState([]);
    const [reservations, setReservations] = useState([]);
    const [closures, setClosures] = useState([]);
    const [newIds, setNewIds] = useState(new Set());
    const { lastEvent } = useWS();
    const navigate = useNavigate();

    const today = romeDateString();

    const load = async () => {
        try {
            const [s, l, r, c] = await Promise.all([
                api.get("/dashboard/stats"),
                api.get("/activity-logs?limit=10").catch(() => ({ data: [] })),
                api.get(`/reservations?date=${today}`).catch(() => ({ data: [] })),
                api.get("/payments/closures?limit=30").catch(() => ({ data: [] })),
            ]);
            setStats(s.data);
            setLogs(l.data);
            setReservations(r.data || []);
            setClosures(c.data || []);
        } catch (_e) { /* noop */ }
    };

    useEffect(() => {
        load();
        const id = setInterval(load, 8000);
        return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Reagisce a webhook reservation.created in tempo reale
    useEffect(() => {
        if (lastEvent?.type === "reservation_webhook" && lastEvent.event === "reservation.created" && lastEvent.reservation_id) {
            setNewIds((prev) => new Set([...prev, lastEvent.reservation_id]));
            // ricarica per pescare la nuova prenotazione
            load();
            // rimuovi badge dopo 60s
            setTimeout(() => {
                setNewIds((prev) => {
                    const next = new Set(prev);
                    next.delete(lastEvent.reservation_id);
                    return next;
                });
            }, 60000);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lastEvent]);

    // Filtro: prenotazioni nelle prossime 2 ore
    const upcomingTwoHours = useMemo(() => {
        const now = new Date();
        const limit = new Date(now.getTime() + 2 * 3600 * 1000);
        return reservations
            .filter((r) => {
                if (!["confermata", "arrivato"].includes(r.status)) return false;
                if (!r.time) return false;
                const dt = new Date(`${r.date}T${r.time}:00`);
                return dt >= now && dt <= limit;
            })
            .sort((a, b) => (a.time || "").localeCompare(b.time || ""));
    }, [reservations]);

    if (!stats) return <div className="text-elegant">Caricamento dashboard...</div>;

    return (
        <div className="space-y-8" data-testid="dashboard-page">
            <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="text-xs uppercase tracking-[0.3em] text-brand mb-2">Panoramica Servizio</div>
                    <h1 className="font-display text-4xl lg:text-5xl font-bold text-warm">Dashboard</h1>
                </div>
                <div className="flex gap-2 flex-wrap">
                    <button onClick={() => navigate("/cassa")} data-testid="dashboard-cassa-button"
                        className="h-12 px-5 bg-brand hover:bg-brand-light text-deep font-display font-bold uppercase tracking-wider rounded-md flex items-center gap-2 transition-transform hover:scale-105 shadow-lg shadow-brand/20">
                        <ReceiptIcon className="w-5 h-5" /> Vai alla Cassa
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <KpiCard testId="kpi-revenue" icon={TrendingUp} label="Incasso Oggi" value={`€ ${stats.revenue_today.toFixed(2)}`} accent="text-brand" hint={`${stats.documents_today} documenti emessi`} />
                <KpiCard testId="kpi-tables" icon={UtensilsCrossed} label="Tavoli Occupati" value={`${stats.occupied_tables}/${stats.total_tables}`} hint={`${stats.open_orders} ordini aperti`} />
                <KpiCard testId="kpi-kitchen" icon={ChefHat} label="In Cucina" value={stats.kitchen_orders} hint="Comande in preparazione" />
                <KpiCard testId="kpi-waiters" icon={Users} label="Camerieri Attivi" value={stats.active_waiters} hint="In servizio oggi" />
            </div>

            <RevenueChart closures={closures} />

            {/* Widget prenotazioni next 2h */}
            <div className="bg-surface border border-[#1f2c44] rounded-lg p-6" data-testid="upcoming-reservations-widget">
                <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-md bg-brand-soft border border-brand/40 flex items-center justify-center text-brand">
                            <BellRing className="w-5 h-5" />
                        </div>
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-elegant">In arrivo</div>
                            <div className="font-display text-2xl font-bold text-warm">Prossime 2 ore</div>
                        </div>
                    </div>
                    <div className="text-elegant text-sm">
                        {upcomingTwoHours.length} {upcomingTwoHours.length === 1 ? "prenotazione" : "prenotazioni"}
                    </div>
                </div>

                {upcomingTwoHours.length === 0 ? (
                    <div className="text-elegant text-sm py-6 text-center">Nessuna prenotazione nelle prossime 2 ore.</div>
                ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                        {upcomingTwoHours.map((r) => {
                            const isNew = newIds.has(r.prenova_id) || newIds.has(r.id) || r.is_new;
                            const dt = new Date(`${r.date}T${r.time}:00`);
                            const minutesUntil = Math.round((dt - new Date()) / 60000);
                            return (
                                <div key={r.id} className={`relative bg-deep border rounded-md p-4 ${
                                    isNew ? "border-brand ring-2 ring-brand/30 animate-pulse" : "border-[#1f2c44]"
                                }`} data-testid={`upcoming-${r.id}`}>
                                    {isNew && (
                                        <div className="absolute -top-2 -right-2 bg-brand text-deep text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded flex items-center gap-1">
                                            <Sparkles className="w-3 h-3" /> NEW
                                        </div>
                                    )}
                                    <div className="flex items-center justify-between gap-2">
                                        <div className="font-display text-2xl font-bold text-brand">{r.time}</div>
                                        <div className="text-[10px] uppercase tracking-wider text-elegant">
                                            {minutesUntil <= 0 ? "ora" : `tra ${minutesUntil}'`}
                                        </div>
                                    </div>
                                    <div className="font-medium text-warm mt-1 truncate">{r.customer_name}</div>
                                    <div className="text-xs text-elegant flex items-center gap-2 mt-1 flex-wrap">
                                        <span><Users className="w-3 h-3 inline" /> {r.people} pers.</span>
                                        {r.table_id && <span>· tavolo assegnato</span>}
                                        {r.source === "Prenova" && <span className="text-brand">· Prenova</span>}
                                    </div>
                                    {r.allergies && (
                                        <div className="text-[10px] text-amber-400 mt-1 truncate">⚠ {r.allergies}</div>
                                    )}
                                    {r.bringing_pet && (
                                        <div className="text-[10px] text-brand mt-1">🐾 con animale</div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Top items */}
                <div className="bg-surface border border-[#1f2c44] rounded-lg p-6 lg:col-span-2">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-elegant">Più Venduti</div>
                            <div className="font-display text-2xl font-bold text-warm">Top Piatti Oggi</div>
                        </div>
                        <ArrowUpRight className="w-5 h-5 text-brand" />
                    </div>
                    {stats.top_items.length === 0 ? (
                        <div className="text-elegant text-sm">Nessun piatto venduto oggi.</div>
                    ) : (
                        <div className="space-y-3">
                            {stats.top_items.map((it, idx) => (
                                <div key={it.name} className="flex items-center gap-4" data-testid={`top-item-${idx}`}>
                                    <div className="w-8 h-8 rounded-md bg-[#1f1f1f] border border-[#1f2c44] flex items-center justify-center text-brand font-bold text-sm">
                                        {idx + 1}
                                    </div>
                                    <div className="flex-1 font-medium">{it.name}</div>
                                    <div className="text-brand font-display font-bold text-lg">×{it.qty}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Reservations */}
                <div className="bg-surface border border-[#1f2c44] rounded-lg p-6">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-elegant mb-1">Prenotazioni</div>
                    <div className="font-display text-2xl font-bold text-warm mb-6">Calendario</div>
                    <div className="space-y-4">
                        <div className="flex items-center gap-3">
                            <Calendar className="w-5 h-5 text-brand" />
                            <div className="flex-1">
                                <div className="text-sm">Oggi</div>
                                <div className="text-xs text-elegant">prenotazioni confermate</div>
                            </div>
                            <div className="font-display text-2xl font-bold text-brand" data-testid="reservations-today">{stats.reservations_today}</div>
                        </div>
                        <div className="flex items-center gap-3">
                            <Clock className="w-5 h-5 text-elegant" />
                            <div className="flex-1">
                                <div className="text-sm">Domani</div>
                                <div className="text-xs text-elegant">prenotazioni confermate</div>
                            </div>
                            <div className="font-display text-2xl font-bold text-warm" data-testid="reservations-tomorrow">{stats.reservations_tomorrow}</div>
                        </div>
                    </div>
                </div>

                {/* Payment methods */}
                <div className="bg-surface border border-[#1f2c44] rounded-lg p-6 lg:col-span-2">
                    <div className="flex items-center justify-between mb-6">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.2em] text-elegant">Pagamenti</div>
                            <div className="font-display text-2xl font-bold text-warm">Metodi Oggi</div>
                        </div>
                        <FileText className="w-5 h-5 text-brand" />
                    </div>
                    {stats.payment_methods.length === 0 ? (
                        <div className="text-elegant text-sm">Nessun pagamento ricevuto oggi.</div>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                            {stats.payment_methods.map((p) => (
                                <div key={p.method} className="bg-surface-elevated border border-[#1f2c44] rounded-md p-3">
                                    <div className="text-[10px] uppercase tracking-wider text-elegant">{p.method}</div>
                                    <div className="font-display font-bold text-lg text-warm">€ {p.amount.toFixed(2)}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Activity */}
                <div className="bg-surface border border-[#1f2c44] rounded-lg p-6">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-elegant mb-1">Attività</div>
                    <div className="font-display text-2xl font-bold text-warm mb-4">Ultime azioni</div>
                    <div className="space-y-2 text-xs">
                        {logs.slice(0, 6).map((l, i) => (
                            <div key={i} className="border-l-2 border-[#1f2c44] pl-3 py-1">
                                <div className="text-warm">{l.action}</div>
                                <div className="text-elegant text-[10px]">
                                    {new Date(l.timestamp).toLocaleTimeString("it-IT")}
                                </div>
                            </div>
                        ))}
                        {logs.length === 0 && <div className="text-elegant">Nessuna attività recente.</div>}
                    </div>
                </div>
            </div>
        </div>
    );
};

const RevenueChart = ({ closures }) => {
    const [period, setPeriod] = useState("week"); // week, month
    const data = useMemo(() => {
        const now = new Date();
        const days = period === "week" ? 7 : 30;
        const buckets = [];
        for (let i = days - 1; i >= 0; i--) {
            const d = new Date(now);
            d.setDate(d.getDate() - i);
            const key = d.toISOString().slice(0, 10);
            const closure = closures.find((c) => c.date === key);
            buckets.push({
                date: key,
                label: d.toLocaleDateString("it-IT", { day: "2-digit", month: period === "week" ? "short" : "numeric" }),
                amount: closure ? closure.summary.total_amount : 0,
                docs: closure ? closure.summary.documents_count : 0,
            });
        }
        return buckets;
    }, [closures, period]);

    const maxVal = Math.max(...data.map((d) => d.amount), 1);
    const totalPeriod = data.reduce((s, d) => s + d.amount, 0);
    const avgPeriod = totalPeriod / data.length;
    const daysWithData = data.filter((d) => d.amount > 0).length;

    return (
        <div className="bg-surface border border-[#1f2c44] rounded-lg p-6" data-testid="revenue-chart">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-5">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-md bg-brand-soft border border-brand/40 flex items-center justify-center text-brand">
                        <BarChart3 className="w-5 h-5" />
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.2em] text-elegant">Trend Incassi</div>
                        <div className="font-display text-2xl font-bold text-warm">
                            {period === "week" ? "Ultimi 7 giorni" : "Ultimi 30 giorni"}
                        </div>
                    </div>
                </div>
                <div className="flex border border-[#1f2c44] rounded overflow-hidden">
                    <button onClick={() => setPeriod("week")} data-testid="chart-period-week"
                        className={`px-4 h-10 text-xs uppercase tracking-wider ${period === "week" ? "bg-brand text-deep font-bold" : "text-elegant"}`}>
                        Settimana
                    </button>
                    <button onClick={() => setPeriod("month")} data-testid="chart-period-month"
                        className={`px-4 h-10 text-xs uppercase tracking-wider ${period === "month" ? "bg-brand text-deep font-bold" : "text-elegant"}`}>
                        Mese
                    </button>
                </div>
            </div>

            <div className="grid grid-cols-3 gap-3 mb-4 text-xs">
                <div className="bg-deep border border-[#1f2c44] rounded p-3">
                    <div className="text-[10px] uppercase tracking-wider text-elegant">Totale {period === "week" ? "settimana" : "mese"}</div>
                    <div className="font-display text-xl font-bold text-brand mt-1" data-testid="chart-total">€ {totalPeriod.toFixed(2)}</div>
                </div>
                <div className="bg-deep border border-[#1f2c44] rounded p-3">
                    <div className="text-[10px] uppercase tracking-wider text-elegant">Media giornaliera</div>
                    <div className="font-display text-xl font-bold text-warm mt-1">€ {avgPeriod.toFixed(2)}</div>
                </div>
                <div className="bg-deep border border-[#1f2c44] rounded p-3">
                    <div className="text-[10px] uppercase tracking-wider text-elegant">Giorni con chiusura</div>
                    <div className="font-display text-xl font-bold text-warm mt-1">{daysWithData}/{data.length}</div>
                </div>
            </div>

            {totalPeriod === 0 ? (
                <div className="text-elegant text-sm py-8 text-center border border-dashed border-[#1f2c44] rounded">
                    Nessuna chiusura corrispettivi nel periodo. Esegui la chiusura giornaliera in Cassa per popolare il grafico.
                </div>
            ) : (
                <div className="flex items-end gap-1 h-40 border-b border-[#1f2c44]" data-testid="chart-bars">
                    {data.map((d) => {
                        const h = (d.amount / maxVal) * 100;
                        return (
                            <div key={d.date} className="flex-1 flex flex-col items-center gap-1 group" title={`${d.date}: € ${d.amount.toFixed(2)} (${d.docs} doc.)`}>
                                <div className="text-[9px] text-elegant opacity-0 group-hover:opacity-100 transition-opacity">€ {d.amount.toFixed(0)}</div>
                                <div className={`w-full rounded-t transition-all ${d.amount > 0 ? "bg-brand hover:bg-brand-light" : "bg-[#1f2c44]"}`}
                                     style={{ height: `${Math.max(h, 2)}%` }} />
                            </div>
                        );
                    })}
                </div>
            )}
            <div className="flex gap-1 mt-2">
                {data.map((d, i) => (
                    <div key={d.date} className="flex-1 text-center">
                        {(period === "week" || i % Math.ceil(data.length / 7) === 0) && (
                            <div className="text-[9px] text-elegant truncate">{d.label}</div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default Dashboard;

// Toggle riconoscimento vocale (persistito in localStorage)
const VoiceModeToggle = () => {
    const [enabled, setEnabled] = React.useState(() => localStorage.getItem("voice_order_enabled") === "1");
    const toggle = () => {
        const next = !enabled;
        setEnabled(next);
        localStorage.setItem("voice_order_enabled", next ? "1" : "0");
        if (typeof window !== "undefined") {
            window.dispatchEvent(new CustomEvent("voice_order_changed", { detail: next }));
        }
    };
    return (
        <button onClick={toggle}
            data-testid="voice-mode-toggle"
            className={`h-12 px-5 font-display font-bold uppercase tracking-wider rounded-md flex items-center gap-2 transition-all border-2 ${
                enabled
                    ? "bg-purple-600 border-purple-400 text-white shadow-lg shadow-purple-600/30"
                    : "bg-deep border-purple-500/40 text-purple-300 hover:bg-purple-900/20"
            }`}>
            {enabled ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
            {enabled ? "Riconoscimento vocale ATTIVO" : "Abilita riconoscimento vocale"}
        </button>
    );
};
